import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Activity, Boxes, Building2, ClipboardList, FileText, Home, LayoutGrid, LogOut, Menu,
  Package, Receipt, Search, Settings, ShoppingCart, Sparkles, Truck, Users, Wallet, X,
  Bell, Bot, IndianRupee, FolderKanban, Factory, ShieldCheck, Loader2, Wrench, Zap,
  Clock, UserCheck, ScanFace, Navigation, Award, BellRing, FileSpreadsheet, PhoneCall,
  Upload, FileArchive, BadgeCheck, TrendingUp,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/context/BrandingContext";
import { api, logError } from "@/lib/api";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import { DailyQuote } from "@/components/DailyQuote";
import { TaskPopup } from "@/components/TaskPopup";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Dashboard", icon: Home, module: "dashboard" },
  { to: "/leads", label: "Lead Pipeline", icon: LayoutGrid, module: "leads" },
  { to: "/customers", label: "Customers", icon: Users, module: "customers" },
  { to: "/products", label: "Products", icon: Package, module: "products" },
  { to: "/quotations", label: "Quotations", icon: FileText, module: "quotations" },
  { to: "/orders", label: "Sales Orders", icon: ClipboardList, module: "sales_orders" },
  { to: "/invoices", label: "Invoices", icon: Receipt, module: "invoices" },
  { to: "/receivables", label: "Receivables", icon: IndianRupee, module: "payments" },
  { to: "/collections", label: "Collections", icon: PhoneCall, module: "payments" },
  { to: "/ai-collections", label: "AI Collections", icon: Sparkles, module: "payments" },
  { to: "/lead-roi", label: "Lead ROI", icon: TrendingUp, module: "leads" },
  { to: "/approvals", label: "Approval Center", icon: BadgeCheck, module: "customers" },
  { to: "/reminders", label: "Payment Reminders", icon: BellRing, module: "payments" },
  { to: "/gst-reports", label: "GST Reports", icon: FileSpreadsheet, module: "reports" },
  { to: "/contact-import", label: "Import Contacts", icon: Upload, module: "leads" },
  { to: "/ca-export", label: "CA / GST Export", icon: FileArchive, module: "reports" },
  { to: "/payments", label: "Payments", icon: Receipt, module: "payments" },
  { to: "/manufacturing", label: "Manufacturing", icon: Factory, module: "production" },
  { to: "/dispatch", label: "Dispatch", icon: Truck, module: "dispatch" },
  { to: "/service", label: "Service & Warranty", icon: Wrench, module: "warranty" },
  { to: "/work", label: "Work", icon: ClipboardList },
  { to: "/my", label: "My Attendance", icon: Clock },
  { to: "/hr", label: "HR & Attendance", icon: UserCheck, module: "hr" },
  { to: "/kiosk", label: "Attendance Kiosk", icon: ScanFace, module: "attendance" },
  { to: "/tracking", label: "Live Tracking", icon: Navigation, module: "attendance" },
  { to: "/payroll", label: "Payroll", icon: Wallet, module: "payroll" },
  { to: "/performance", label: "Performance", icon: Award, module: "hr" },
  { to: "/expenses", label: "Expenses", icon: Wallet, module: "expenses" },
  { to: "/inventory", label: "Inventory", icon: Boxes, module: "inventory" },
  { to: "/purchase", label: "Purchase", icon: ShoppingCart, module: "purchase" },
  { to: "/suppliers", label: "Suppliers", icon: Truck, module: "suppliers" },
  { to: "/projects", label: "Projects", icon: FolderKanban, module: "projects" },
  { to: "/ai-requests", label: "AI Requests", icon: Sparkles, module: "ai" },
  { to: "/assistant", label: "AI Assistant", icon: Bot, module: "ai" },
  { to: "/reports", label: "Reports", icon: Activity, module: "reports" },
  { to: "/employees", label: "Employees", icon: Building2, module: "employees" },
  { to: "/permissions", label: "Permissions", icon: ShieldCheck, module: "settings", ownerOnly: true },
  { to: "/data-vault", label: "Data Vault", icon: ShieldCheck, ownerOnly: true },
  { to: "/system-health", label: "System Health", icon: Activity, ownerOnly: true },
  { to: "/eway-settings", label: "E-Way Bill API", icon: ShieldCheck, ownerOnly: true },
  { to: "/automation", label: "Automation", icon: Zap, module: "automation" },
  { to: "/settings", label: "Settings", icon: Settings, module: "settings" },
];

const MOBILE_NAV = [
  { to: "/", label: "Home", icon: Home, module: "dashboard" },
  { to: "/quotations", label: "Quotes", icon: FileText, module: "quotations" },
  { to: "/leads", label: "Leads", icon: LayoutGrid, module: "leads" },
  { to: "/inventory", label: "Stock", icon: Boxes, module: "inventory" },
  { to: "/ai-requests", label: "AI", icon: Sparkles, module: "ai" },
];

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setResults(data.results || []);
        setOpen(true);
      } catch {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="relative w-full max-w-md">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        data-testid="global-search-input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        placeholder="Search customers, quotations, stock, GSTIN…"
        className="pl-9"
      />
      {open && results.length > 0 && (
        <div
          data-testid="global-search-results"
          className="absolute z-50 mt-2 max-h-80 w-full overflow-auto rounded-lg border border-border bg-popover shadow-lg"
        >
          {results.map((r) => (
            <button
              key={`${r.type}-${r.id}`}
              data-testid={`search-result-${r.id}`}
              onMouseDown={() => {
                navigate(r.link);
                setQ("");
              }}
              className="flex w-full items-center justify-between gap-3 border-b border-border px-3 py-2 text-left text-sm last:border-0 hover:bg-secondary"
            >
              <span className="truncate">{r.title}</span>
              <span className="shrink-0 text-[10px] uppercase tracking-wider text-muted-foreground">
                {r.type}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, can } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const location = useLocation();

  const items = useMemo(
    () => NAV.filter((n) => (n.ownerOnly ? user?.role === "super_admin"
      : n.module ? can(n.module) : true)),
    [can, user]
  );

  useEffect(() => {
    api
      .get("/notifications")
      .then(({ data }) => setNotes(data))
      .catch((e) => logError("notifications", e));
  }, [location.pathname]);

  const unread = notes.filter((n) => !n.read).length;

  const SidebarBody = (
    <div className="flex h-full flex-col sidebar-surface">
      <Link
        to="/"
        data-testid="brand-link"
        className="flex flex-col items-center gap-2 border-b border-border px-5 py-5 text-center"
      >
        <BrandMark className="h-14 w-14" showText={false} />
        <span className="rail-brand text-[11px] font-semibold uppercase tracking-[0.22em]">
          Pramsia Global Ventures
        </span>
      </Link>
      <nav className="flex-1 space-y-0.5 overflow-y-auto px-2 py-3">
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-full px-3 py-2 text-sm transition-all ${
                isActive
                  ? "bg-primary font-bold text-primary-foreground"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`
            }
          >
            <n.icon className="h-4 w-4 shrink-0" />
            <span className="truncate">{n.label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border p-3">
        <div className="mb-2 px-2">
          <div className="truncate text-sm font-medium">{user?.name}</div>
          <div className="truncate text-[11px] text-muted-foreground">{user?.role_label}</div>
        </div>
        <Button
          data-testid="sidebar-logout-button"
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-2 text-muted-foreground"
          onClick={logout}
        >
          <LogOut className="h-4 w-4" /> Sign out
        </Button>
      </div>
    </div>
  );

  const mobileItems = useMemo(() => MOBILE_NAV.filter((n) => can(n.module)), [can]);

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 border-r border-border bg-card lg:block">
        {SidebarBody}
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-64 p-0">
          {SidebarBody}
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <div id="quote-banner-slot" />
        <TaskPopup />
        <header className="glass-bar sticky top-0 z-40 flex items-center gap-3 px-4 py-3">
          <Button
            data-testid="mobile-menu-button"
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-2">
            <DailyQuote />
            <ThemeSwitcher />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="notifications-button" variant="ghost" size="icon" className="relative">
                  <Bell className="h-5 w-5" />
                  {unread > 0 && (
                    <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[9px] font-bold text-accent-foreground">
                      {unread}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notes.length === 0 && (
                  <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                    Nothing new right now
                  </div>
                )}
                {notes.slice(0, 10).map((n) => (
                  <DropdownMenuItem key={n.id} className="flex-col items-start gap-0.5">
                    <span className="text-sm font-medium">{n.title}</span>
                    <span className="text-xs text-muted-foreground">{n.body}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Badge variant="outline" className="hidden font-mono-num text-[10px] sm:inline-flex">
              {user?.employee_code || "PRM"}
            </Badge>
          </div>
        </header>

        <main className="min-w-0 flex-1 pb-20 lg:pb-6">{children}</main>

        <nav className="fixed bottom-0 left-0 right-0 z-40 grid grid-cols-5 border-t border-border bg-background/95 backdrop-blur-xl lg:hidden">
          {mobileItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`mobile-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2.5 text-[10px] ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`
              }
            >
              <n.icon className="h-5 w-5" />
              {n.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions, testId }) {
  return (
    <div
      data-testid={testId}
      className="flex flex-col gap-3 border-b border-border px-4 py-5 sm:flex-row sm:items-end sm:justify-between sm:px-6"
    >
      <div>
        <div className="overline text-accent">Pramsia Command Center</div>
        <h1 className="font-display text-2xl font-bold tracking-wider sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Empty({ title, hint, action }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 border border-dashed border-border p-12 text-center">
      <X className="h-8 w-8 text-muted-foreground/50" />
      <div className="font-medium">{title}</div>
      {hint && <div className="max-w-sm text-sm text-muted-foreground">{hint}</div>}
      {action}
    </div>
  );
}

/** Renders a spinner while loading, an empty state when there is nothing, otherwise the list. */
export function ListState({ loading, isEmpty, emptyTitle, emptyHint, emptyAction, children }) {
  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (isEmpty) {
    return <Empty title={emptyTitle} hint={emptyHint} action={emptyAction} />;
  }
  return children;
}

